import { useState, useEffect, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { contactsApi } from '@/services/api';
import type { Contact } from '@/types';

interface ContactPickerProps {
  onSelect: (contact: Contact) => void;
  placeholder?: string;
}

/**
 * Kontakt-Sucher mit Dropdown: debounced-Suche gegen contactsApi,
 * Top-10-Ergebnisse, Klick übergibt den Kontakt an die Eltern-Komponente.
 */
export function ContactPicker({ onSelect, placeholder = 'Kontakt suchen…' }: ContactPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [results, setResults] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearch.trim() === '') {
      setResults([]);
      setIsOpen(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    contactsApi
      .getAll(1, debouncedSearch)
      .then((response) => {
        if (cancelled) return;
        setResults(response.data.data.slice(0, 10));
        setIsOpen(true);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  // Klick außerhalb schließt das Dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (contact: Contact) => {
    onSelect(contact);
    setSearchTerm('');
    setDebouncedSearch('');
    setResults([]);
    setIsOpen(false);
  };

  const displayName = (contact: Contact): string =>
    contact.display_name ||
    contact.company_name ||
    [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
    contact.email_address ||
    'Unbenannt';

  return (
    <div ref={containerRef} className="relative w-full sm:max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        {isLoading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />}
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="input pl-9"
        />
      </div>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
          {results.length === 0 && !isLoading && (
            <p className="px-3 py-2 text-sm text-gray-500">Keine Kontakte gefunden</p>
          )}
          {results.map((contact) => (
            <button
              key={contact.id}
              type="button"
              onClick={() => handleSelect(contact)}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
            >
              <span className="font-medium text-gray-900">{displayName(contact)}</span>
              {contact.company_name && contact.display_name && (
                <span className="ml-1 text-gray-500">· {contact.company_name}</span>
              )}
              {contact.email_address && (
                <span className="block truncate text-xs text-gray-500">{contact.email_address}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}